import { isPlainObject } from 'lodash-es';
import log from 'loglevel';
import { onCallGlobal } from './modules/utils/calls/index.js';
import { typeofOrNull } from './modules/utils/index.js';
const createStateReplacer = (newState) => () => newState;
export default function createCall(context, initState) {
    if (!isPlainObject(context)) {
        throw new Error(`
      createCall expected context argument to be a plain object but
      instead received an object with type ${typeofOrNull(context)}.
      the best way to supply a valid context is to use the createContext
      function provided by this library.
    `);
    }
    // set error as default level in absence of specified level by context
    log.setDefaultLevel(context.logLevel || 'error');
    // expose log level setting function
    typeof window !== 'undefined' &&
        (window.setLogLevel = (level) => log.setLevel(level));
    let privateContext = context;
    let privateState = initState || Object.freeze({});
    let privateMock = null;
    let globalApiCallbacks = {};
    function call(callee, ...args) {
        if (privateMock)
            return privateMock(callee, ...args);
        const client = {
            context: privateContext,
            getState,
            replaceState,
            setState,
            globalApiCallbacks,
        };
        try {
            const result = callee(client, ...args);
            if (result instanceof Promise) {
                const promise = result;
                onCallGlobal(client, callee, args, promise);
                return promise;
            }
            return Promise.resolve(result);
        }
        catch (err) {
            return Promise.reject(err);
        }
    }
    // hard set state, called internally in api client
    const replaceState = (newStateOrReplacer) => {
        const replacer = typeof newStateOrReplacer === 'function'
            ? newStateOrReplacer
            : createStateReplacer(newStateOrReplacer);
        const currentState = privateState;
        const nextState = replacer(currentState);
        log.debug('[API-CLIENT] State Replace\nprev state:', currentState, '\nnew State:', nextState);
        privateState = nextState;
        notifyStateListeners(nextState, currentState);
    };
    // merge state without notification, typically called externally by consuming app
    const setState = (partialState) => {
        const newState = {
            ...privateState,
            ...partialState,
        };
        log.debug('[API-CLIENT] State Set\nprev state:', privateState, '\nnew State:', newState);
        privateState = newState;
    };
    const getState = () => privateState;
    const mock = (mockCall) => {
        if (typeof mockCall !== 'function') {
            throw new Error(`
        mock expected the only argument to be a function but
        instead received an object with type ${typeofOrNull(mockCall)}.
      `);
        }
        privateMock = mockCall;
    };
    const unmock = () => {
        privateMock = null;
    };
    call.replaceContext = (context) => {
        privateContext = context;
    };
    call.getContext = () => privateContext;
    call.replaceState = replaceState;
    call.setState = setState;
    call.getState = getState;
    call.mock = mock;
    call.unmock = unmock;
    call.setOnRequest = (onRequest) => {
        globalApiCallbacks = {
            ...globalApiCallbacks,
            onRequest,
        };
    };
    call.setOnResponse = (onResponse) => {
        globalApiCallbacks = {
            ...globalApiCallbacks,
            onResponse,
        };
    };
    let stateListeners = [];
    const makeRemoveStateListener = (cb) => () => {
        stateListeners = stateListeners.filter((l) => l !== cb);
    };
    call.onStateUpdated = (cb) => {
        stateListeners.push(cb);
        return makeRemoveStateListener(cb);
    };
    const notifyStateListeners = (nextState, prevState) => stateListeners.forEach((cb) => cb(nextState, prevState));
    return call;
}
